import { useIsAuthenticated } from '@azure/msal-react';
import { useQuery } from '@tanstack/react-query';
import React, { useCallback, useContext, useMemo, useTransition } from 'react';
import { FEATURES } from '../config/featureConfiguration';
import { CURRENT_ONBOARDING_VERSION } from '../constants/onboardingConstants';
import { getProfile, PROFILE_QUERY_KEY } from '../services/appConfigApi';
import type { AppConfigContextType } from '../types/appConfig';
import { AppConfigContext } from './AppConfigContext';

/**
 * Hook to access application configuration including roles, features, and permissions
 */
export const useAppConfig = (): AppConfigContextType => {
  const context = useContext(AppConfigContext);
  if (!context) {
    throw new Error('useAppConfig must be used within an AppConfigProvider');
  }
  return context;
};

interface AppConfigProviderProps {
  children: React.ReactNode;
}

/**
 * Provider for application configuration with backend-controlled feature flags
 * Features are controlled by the backend via /api/v1/profile/me endpoint
 * This enables per-environment and per-user feature control without frontend deployments
 */
export const AppConfigProvider: React.FC<AppConfigProviderProps> = ({ children }) => {
  const isAuthenticated = useIsAuthenticated();
  const [, startTransition] = useTransition();

  const {
    data: profileData,
    isLoading,
    error: queryError,
    failureCount,
    refetch,
  } = useQuery({
    queryKey: PROFILE_QUERY_KEY,
    queryFn: getProfile,
    enabled: isAuthenticated,
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000), // Exponential backoff
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: true,
  });

  const roles = useMemo(() => profileData?.roles ?? [], [profileData]);

  const featureFlags = useMemo(() => {
    if (!profileData) return {};

    const normalizedFeatures: Record<string, boolean> = {};
    Object.entries(profileData.features).forEach(([key, value]) => {
      const normalizedKey = key.replace(/_/g, '-');
      normalizedFeatures[normalizedKey] = value;
    });

    return normalizedFeatures;
  }, [profileData]);

  const queryErrorMessage = queryError instanceof Error ? queryError.message : 'Failed to load profile features';
  const error = queryError ? queryErrorMessage : null;

  // Filter features based on feature flags
  const availableFeatures = useMemo(() => {
    return FEATURES.filter((feature) => {
      if (feature.id === 'core-navigation') {
        return true;
      }
      return featureFlags[feature.id] === true;
    });
  }, [featureFlags]);

  const availablePanelComponents = useMemo(() => {
    return availableFeatures.flatMap((feature) => feature.panelComponents);
  }, [availableFeatures]);

  const availableWidgets = useMemo(() => {
    return availableFeatures.flatMap((feature) => feature.widgets);
  }, [availableFeatures]);

  const availablePages = useMemo(() => {
    return availableFeatures.flatMap((feature) => feature.pages);
  }, [availableFeatures]);

  const availableMenuItems = useMemo(() => {
    return availablePages.filter((page) => page.showInNavigation !== false);
  }, [availablePages]);

  const canVisitPage = useCallback(
    (path: string): boolean => {
      return availablePages.some((page) => page.path === path);
    },
    [availablePages],
  );

  const canUsePanelComponent = useCallback(
    (componentId: string): boolean => {
      return availablePanelComponents.some((component) => component.id === componentId);
    },
    [availablePanelComponents],
  );

  const canUseWidget = useCallback(
    (widgetId: string): boolean => {
      return availableWidgets.some((widget) => widget.id === widgetId);
    },
    [availableWidgets],
  );

  const canUseFeature = useCallback(
    (featureId: string): boolean => {
      return availableFeatures.some((feature) => feature.id === featureId);
    },
    [availableFeatures],
  );

  const hasRole = useCallback(
    (role: string): boolean => {
      return roles.includes(role);
    },
    [roles],
  );

  const onboardingVersionCompleted = profileData?.onboarding_version_completed ?? null;
  const needsOnboarding =
    onboardingVersionCompleted === null || onboardingVersionCompleted < CURRENT_ONBOARDING_VERSION;

  const value: AppConfigContextType = useMemo(
    () => ({
      featureFlags,
      roles,
      needsOnboarding,
      onboardingVersionCompleted,
      isLoading,
      error,
      retryCount: failureCount,
      onRetry: () => {
        startTransition(() => {
          refetch();
        });
      },
      availableFeatures,
      availablePanelComponents,
      availableWidgets,
      availableMenuItems,
      canVisitPage,
      canUsePanelComponent,
      canUseWidget,
      canUseFeature,
      hasRole,
    }),
    [
      featureFlags,
      roles,
      needsOnboarding,
      onboardingVersionCompleted,
      isLoading,
      error,
      failureCount,
      refetch,
      availableFeatures,
      availablePanelComponents,
      availableWidgets,
      availableMenuItems,
      canVisitPage,
      canUsePanelComponent,
      canUseWidget,
      canUseFeature,
      hasRole,
      startTransition,
    ],
  );

  return <AppConfigContext.Provider value={value}>{children}</AppConfigContext.Provider>;
};
